import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { useKeyPressed } from 'hooks';

interface SwitchUIProps {
  checked?: boolean;
  disabled?: boolean;
  keepStyles?: boolean;
  focused?: boolean;
  isLoading?: boolean;
}

const TRACK_WIDTH = 33;
const TRACK_HEIGHT = 20; // use a multiple of 2 for best results
const HANDLE_SIZE = 16; // use a multiple of 2 for best results
const HANDLE_POS_OFF = (TRACK_HEIGHT - HANDLE_SIZE) / 2;
const HANDLE_POS_ON = TRACK_WIDTH - HANDLE_SIZE - HANDLE_POS_OFF;

const SwitchLayout = styled.div<SwitchUIProps>`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: ${toRem(TRACK_WIDTH)};
  height: ${toRem(TRACK_HEIGHT)};
  background-color: ${theme.color.neutral[400]};
  border-radius: ${toRem(TRACK_WIDTH / 2)};
  cursor: pointer;
  transition: background-color ${theme.transition.button.fast};

  ${({ checked }) =>
    checked &&
    css`
      background-color: ${theme.color.secondary[400]};
    `}

  ${({ focused }) =>
    focused &&
    css`
      border-color: ${theme.color.neutral[400]};
    `}

  ${({ disabled, keepStyles }) =>
    disabled &&
    css`
      background-color: ${!keepStyles && theme.color.neutral[200]};
      cursor: default;
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      cursor: default;
    `}
`;

const SwitchHandle = styled.div<SwitchUIProps>`
  position: absolute;
  top: ${toRem(HANDLE_POS_OFF)};
  left: ${toRem(HANDLE_POS_OFF)};
  width: ${toRem(HANDLE_SIZE)};
  height: ${toRem(HANDLE_SIZE)};
  border-radius: 50%;
  background-color: ${theme.color.white};
  cursor: pointer;
  transform: translateX(0);
  transition: left ${theme.transition.icon};

  ${({ checked }) =>
    checked &&
    css`
      left: ${toRem(HANDLE_POS_ON)};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      cursor: default;
    `}
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
`;

const LoadingSkeleton = styled(Skeleton)`
  && {
    width: ${toRem(TRACK_WIDTH)};
    height: ${toRem(TRACK_HEIGHT)};
    border-radius: ${toRem(TRACK_HEIGHT / 2)};
  }
`;

interface SwitchProps extends SwitchUIProps {
  onChange: () => void;
  className?: string;
  onFocus?: () => void;
  onBlur?: (e: any) => void;
}

const Switch = ({ checked, disabled, isLoading, onChange, onBlur, onFocus, ...rest }: SwitchProps) => {
  // UI States
  const [inputFocus, setInputFocus] = useState<boolean>(false);

  // Functions
  const handleOnChange = () => {
    if (disabled || isLoading) return;
    onChange();
  };

  const handleOnFocus = () => {
    setInputFocus(true);
    onFocus?.();
  };

  const handleOnBlur = (e: any) => {
    setInputFocus(false);
    onBlur?.(e);
  };

  const toggleOnEnter = useKeyPressed((event: KeyboardEvent) => event.key === 'Enter');

  const handleOnKeyUp = () => {
    if (inputFocus && toggleOnEnter) {
      onChange();
    }
  };

  return (
    <SwitchLayout isLoading={isLoading} checked={checked} disabled={disabled || isLoading} focused={inputFocus} onClick={handleOnChange} {...rest}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <SwitchHandle isLoading={isLoading} checked={checked} disabled={disabled} data-cy="toggle" />
          <HiddenInput type="checkbox" role="switch" aria-checked={checked} checked={checked} disabled={disabled || isLoading} {...rest} onFocus={handleOnFocus} onBlur={handleOnBlur} onKeyUp={handleOnKeyUp} onChange={handleOnChange} />
        </>
      )}
    </SwitchLayout>
  );
};

export default Switch;
